import { getCartItemBasePricePerGuest, readSessionCheckoutTax } from '@/utils/specialExtra';
import { normalizeOptionalQuantities } from '@/utils/optionalOverrides';
import { claimSharedQuote } from '@/utils/claimSharedQuote';
import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CartItem } from "@/utils/cartStorage";
import { toast } from "@/hooks/use-toast";
import { incrementReferralCount } from "@/services/referralService";
import { ensureAnonymousSession } from "@/utils/anonymousSession";
import { buildSpecialGuestsSnapshot } from "@/utils/specialGuests";
import { isOrderLockedForCart } from "@/utils/orderLock";
import { findOpenOrderToReuse } from "@/utils/openOrderReuse";
import { recordCreationOrigin } from "@/utils/creationOrigin";
import { freightExtraColumns } from "@/utils/freightExtra";
import { buildEventHourColumns } from "@/utils/eventHourFields";

/** Lê a etapa e o pagamento do pedido para saber se o carrinho ainda pode alterá-lo. */
const isOrderSold = async (eventId: string | null | undefined): Promise<boolean> => {
  if (!eventId || !isValidUUID(eventId)) return false;
  const { data } = await supabase.from("events").select("status, status_payments").eq("id", eventId).maybeSingle();
  return isOrderLockedForCart(data);
};

const isValidUUID = (val: string | null | undefined): boolean => {
  if (!val) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
};

/**
 * Converte as horas adicionais escolhidas pelo cliente (armazenadas no item do
 * carrinho) nas colunas de `events` consumidas por contrato, Kanban e
 * consolidação financeira. Sem horas extras devolve as colunas nulas, para limpar o que estava gravado.
 */
const buildEventHourFields = (cartItem: CartItem | null | undefined): Record<string, any> => buildEventHourColumns(cartItem);

/**
 * Resolve o user_id efetivo para persistência:
 * 1. Se já temos um userId válido vindo do caller, usa ele.
 * 2. Caso contrário, garante uma sessão anônima e usa o id resultante.
 * Em qualquer falha, retorna undefined — fallbacks existentes (sem user_id)
 * continuam funcionando.
 */
const resolveOwnerUserId = async (userId?: string): Promise<string | undefined> => {
  if (isValidUUID(userId)) return userId;
  try {
    const anonId = await ensureAnonymousSession();
    return anonId || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Generate a short event code: 3 uppercase letters from supplier name + "-" + 4 hex chars.
 * Example: "CHU-7F3A" for "Churrasco do Zé"
 */
const generateShortEventCode = (supplierName?: string): string => {
  const clean = (supplierName || "AME")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-zA-Z]/g, "")       // keep only letters
    .toUpperCase();
  const prefix = (clean.slice(0, 3) || "AME").padEnd(3, "X");
  const hex = Math.random().toString(16).slice(2, 6).toUpperCase();
  return `${prefix}-${hex}`;
};

const isLoginStatus = (status?: string | null): boolean => {
  if (!status) return false;
  return status.trim().toLowerCase() === "login";
};

const isQuoteMode = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("mode") === "orcamento" || sessionStorage.getItem("ameup_quote_mode") === "true";
};

/**
 * Resolve o fornecedor do evento pai quando o contexto ainda não o entregou.
 * Ordem de resolução (barata → cara):
 *   1. itens do carrinho na sessão (já carregam supplierId do produto);
 *   2. slug da loja/cardápio visitado (sessionStorage ou URL /loja/:slug),
 *      consultado na view pública `suppliers_public` (segura para anônimos).
 * Retorna `null` quando nada resolve — nesse caso o pai NÃO deve ser criado.
 */
const resolveSupplierContext = async (): Promise<{ id: string; name?: string } | null> => {
  if (typeof window === "undefined") return null;

  // 1. Carrinho da sessão
  try {
    const raw = sessionStorage.getItem("supplier_cart_items");
    if (raw) {
      const items = JSON.parse(raw);
      const withSupplier = Array.isArray(items)
        ? items.find((i: any) => isValidUUID(i?.supplierId))
        : null;
      if (withSupplier) {
        return { id: withSupplier.supplierId, name: withSupplier.supplierName || undefined };
      }
    }
  } catch {
    /* sessionStorage corrompido — segue para o próximo fallback */
  }

  // 2. Slug da loja/cardápio
  let slug: string | null = null;
  try {
    slug = sessionStorage.getItem("ameup_last_supplier_slug");
  } catch {
    slug = null;
  }
  if (!slug) {
    const match = window.location.pathname.match(/\/(?:loja|cardapio)\/([^/?#]+)/);
    slug = match?.[1] || null;
  }
  if (!slug) return null;

  try {
    const { data } = await (supabase as any)
      .from("suppliers_public")
      .select("id, name")
      .eq("slug", slug)
      .maybeSingle();
    if (data?.id && isValidUUID(data.id)) {
      return { id: data.id, name: data.name || undefined };
    }
  } catch {
    /* falha de rede/policy: mantém null */
  }
  return null;
};

/**
 * Garante que o evento pai tenha `supplier_id`/`created_by_supplier_id`.
 * Rede de segurança executada durante a sincronização dos itens do carrinho:
 * mesmo que o pai tenha nascido sem fornecedor, ele é corrigido antes do checkout.
 */
const backfillParentSupplier = async (parentEventId: string, supplierId?: string): Promise<void> => {
  if (!isValidUUID(parentEventId) || !isValidUUID(supplierId)) return;
  try {
    const { data: parent } = await supabase
      .from("events")
      .select("supplier_id, created_by_supplier_id")
      .eq("id", parentEventId)
      .maybeSingle();
    if (!parent) return;
    const patch: Record<string, any> = {};
    if (!parent.supplier_id) patch.supplier_id = supplierId;
    if (!parent.created_by_supplier_id) patch.created_by_supplier_id = supplierId;
    if (Object.keys(patch).length === 0) return;

    patch.updated_at = new Date().toISOString();
    const { error } = await supabase.from("events").update(patch).eq("id", parentEventId);
    if (error) {
      console.warn("⚠️ Não foi possível preencher o fornecedor no evento pai:", error.code);
    } else {
      console.info("🩹 Fornecedor preenchido no evento pai via carrinho:", parentEventId);
    }
  } catch (err) {
    console.warn("⚠️ Erro ao preencher fornecedor no evento pai:", err);
  }
};

/**
 * Hook for persisting event data to Supabase following the Parent-Child pattern.
 *
 * PARENT row (events): stores simulator data (CEP, date, guests) + aggregated totals + short event_code.
 * CHILD rows (events with parent_event_id): one per cart item, linked to parent + supplier.
 *   Children use `origem` to store the product reference (supplier_menu.id) for lookups.
 *   For selectable menus, each selected sub-item creates its own child row with origem = "parentProductId:childItemId".
 */

export interface ParentEventData {
  cep: string;
  eventDate: string;        // ISO date e.g. "2026-02-27"
  guests: string;           // e.g. "11"
  supplierId?: string;
  userId?: string;
  supplierName?: string;    // Used to generate short event_code
  createdBySupplierId?: string; // Track which supplier originated this event
  isPickup?: boolean;
  pickupAddress?: string;
}

// Module-level lock map to serialize concurrent upserts of the same (parent, origem) pair
// Prevents race conditions where two simultaneous calls both read "no existing row" and insert duplicates.
const childUpsertLocks = new Map<string, Promise<string | null>>();

// Mesmo problema para o PAI: o efeito do simulador e o "adicionar ao carrinho" podem chamar
// upsertParentEvent ao mesmo tempo, ambos veem "sem pedido" e inserem — dois pedidos para um carrinho.
// Enquanto um pedido está sendo criado, as demais chamadas esperam e reaproveitam o id criado.
let parentCreateLock: Promise<unknown> | null = null;

export const useEventPersistence = () => {
  const parentIdRef = useRef<string | null>(null);

  // ──────────────────────────────────────────────
  // 1. PARENT: upsert simulator data
  // ──────────────────────────────────────────────
  const upsertParentEventInner = useCallback(async (data: ParentEventData, opts?: { skipReuse?: boolean }): Promise<{ id: string; eventCode?: string } | null> => {
    try {
      // Check sessionStorage as cross-component bridge to avoid duplicate events
      if (!parentIdRef.current) {
        const stored = sessionStorage.getItem('ameup_parent_event_id');
        if (stored) parentIdRef.current = stored;
      }

      // Pedido já pago/vendido: o carrinho não regrava nada (evita sobrescrever valor e frete de venda confirmada).
      if (parentIdRef.current && (await isOrderSold(parentIdRef.current))) {
        return { id: parentIdRef.current, eventCode: sessionStorage.getItem('ameup_event_code') || undefined };
      }

      if (parentIdRef.current && isValidUUID(data.supplierId)) {
        const { data: storedEvent } = await supabase
          .from("events")
          .select("supplier_id, created_by_supplier_id")
          .eq("id", parentIdRef.current)
          .maybeSingle();

        const belongsToSupplier = storedEvent && (
          storedEvent.supplier_id === data.supplierId ||
          storedEvent.created_by_supplier_id === data.supplierId
        );

        // SELECT vazio NÃO significa "pedido de outro fornecedor": pode ser uma proposta do fornecedor
        // (user_id nulo, ilegível pelo cliente por RLS) ou uma sessão que ainda não carregou o JWT
        // (a leitura sai como anônima). Descartar o id nesses casos criava um pedido novo a cada
        // navegação, deixando vários rascunhos do mesmo carrinho. Só descartamos quando o pedido
        // é legível e pertence a OUTRO fornecedor; se a atualização não afetar nenhuma linha, mais
        // abaixo o id é descartado e um novo pedido é criado (uma única vez).
        if (!storedEvent) {
          if (sessionStorage.getItem('ameup_shared_parent_id') === parentIdRef.current) {
            // Passa a ser dono da proposta para conseguir atualizá-la (pai e filhos).
            await claimSharedQuote({ eventId: parentIdRef.current });
          }
        } else if (!belongsToSupplier) {
          parentIdRef.current = null;
          sessionStorage.removeItem('ameup_parent_event_id');
          sessionStorage.removeItem('ameup_event_code');
        }
      }

      const eventId = parentIdRef.current || crypto.randomUUID();

      const payload: Record<string, any> = {
        id: eventId,
        updated_at: new Date().toISOString(),
      };

      // Sessão de proposta compartilhada: garante o flag share no pedido para
      // que os indicadores classifiquem a venda como "cardápio compartilhado"
      // mesmo quando o pedido é criado/reutilizado fora do fluxo de share. A sessão que abriu a proposta
      // (`ameup_shared_parent_id`) também conta: o cliente que perde o `mode=orcamento` (nova aba, voltar do
      // banco) criava um pedido paralelo sem a marca e a venda aparecia como "online".
      if (isQuoteMode() || sessionStorage.getItem('ameup_shared_parent_id')) {
        payload.share = true;
      }

      // Only set status="Preencheu o simulador" on INSERT or when current status
      // is still in the very first funnel stage. Never overwrite later stages
      // (e.g. "Escolheu itens", "Detalhes da Festa", "Aguardando Pagamento", etc.)
      // set by downstream flows like syncCartToSupabase.
      if (!parentIdRef.current) {
        payload.status = isQuoteMode() ? "Proposta Fornecedor" : "Preencheu o simulador";
        payload.geral_status = payload.status;
      } else {
        const { data: existing } = await supabase
          .from("events")
          .select("status")
          .eq("id", eventId)
          .maybeSingle();
        const currentStatus = existing?.status || '';
        
        if (isQuoteMode()) {
          // In quote mode, we always ensure the status is "Proposta Fornecedor" 
          // unless it's already a later stage (which shouldn't happen in this mode)
          const quoteStatuses = ['', 'Acessou Página', 'Preencheu o simulador', 'Entrou no Cardápio', 'Escolheu itens'];
          if (quoteStatuses.includes(currentStatus)) {
            payload.status = "Proposta Fornecedor";
            payload.geral_status = "Proposta Fornecedor";
          }
        } else if (existing && (!currentStatus || currentStatus === 'Acessou Página' || currentStatus === 'Proposta Fornecedor')) {
          // `existing` nulo = pedido ilegível agora (RLS/sessão): não mexe no status para não regredir a etapa.
          payload.status = "Preencheu o simulador";
        }
      }
      // Only include fields that have actual values (avoid overwriting with empty strings)
      const cepClean = data.cep?.replace(/\D/g, '');
      if (cepClean && cepClean.length === 8) payload.cep = cepClean;
      if (data.eventDate) payload.event_date = data.eventDate;
      if (data.guests && data.guests !== '0') payload.guests = data.guests;
      if (data.isPickup !== undefined) {
        payload.is_pickup = data.isPickup;
        if (data.isPickup) {
          payload.delivery_fee = 0;
          if (data.pickupAddress?.trim()) payload.pickup_address = data.pickupAddress.trim();
        } else {
          payload.pickup_address = null;
        }
      }

      // Fornecedor efetivo: o informado pelo caller ou, se o contexto ainda não
      // carregou, resolvido pelo carrinho/slug da loja. Sem fornecedor, o pai
      // nasceria órfão e travaria o checkout ("Formas de pagamento indisponíveis").
      let effectiveSupplierId = isValidUUID(data.supplierId) ? data.supplierId! : undefined;
      let effectiveSupplierName = data.supplierName;

      if (!effectiveSupplierId) {
        const resolved = await resolveSupplierContext();
        if (resolved) {
          effectiveSupplierId = resolved.id;
          if (!effectiveSupplierName) effectiveSupplierName = resolved.name;
          console.info("🔎 Fornecedor resolvido por contexto para o evento pai:", effectiveSupplierId);
        }
      }

      if (effectiveSupplierId) payload.supplier_id = effectiveSupplierId;
      const ownerId = await resolveOwnerUserId(data.userId);
      if (isValidUUID(ownerId)) payload.user_id = ownerId;

      let generatedCode: string | undefined;

      if (!parentIdRef.current) {
        // Sem ponteiro de pedido na sessão (ex.: o cliente voltou do banco numa aba nova): continua no pedido aberto da mesma
        // conta, fornecedor e data em vez de criar um paralelo com os mesmos itens (MAR-3CE8 x proposta MAR-5B23).
        if (!opts?.skipReuse && isValidUUID(ownerId) && effectiveSupplierId && data.eventDate) {
          const reusable = await findOpenOrderToReuse({ userId: ownerId as string, supplierId: effectiveSupplierId, eventDate: data.eventDate });
          if (reusable) {
            parentIdRef.current = reusable.id;
            try {
              sessionStorage.setItem('ameup_parent_event_id', reusable.id);
              if (reusable.event_code) sessionStorage.setItem('ameup_event_code', reusable.event_code);
            } catch { /* sem sessionStorage */ }
            console.info('♻️ Pedido aberto da conta reaproveitado em vez de criar um novo:', reusable.event_code || reusable.id);
            return await upsertParentEventInner(data, { skipReuse: true });
          }
        }
        // Nunca criar um evento pai sem fornecedor: o registro ficaria órfão e
        // impediria o carregamento das formas de pagamento no checkout.
        if (!effectiveSupplierId) {
          console.warn("⚠️ Criação do evento pai adiada: fornecedor ainda não resolvido");
          return null;
        }

        // New parent: generate short event_code using supplier name
        let nameForCode = effectiveSupplierName;
        // If no name provided but we have supplierId, fetch it from the public view
        if (!nameForCode) {
          const { data: sup } = await (supabase as any)
            .from("suppliers_public")
            .select("name")
            .eq("id", effectiveSupplierId)
            .maybeSingle();
          nameForCode = sup?.name || undefined;
        }
        generatedCode = generateShortEventCode(nameForCode);
        payload.event_code = generatedCode;
        payload.created_at = new Date().toISOString();
        if (!payload.created_by_supplier_id) payload.created_by_supplier_id = effectiveSupplierId;
      }

      // Add referral tracking if present (both insert and update)
      const referredBy = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ameup_referred_by') : null;
      if (referredBy) payload.referred_by = referredBy;

      if (!parentIdRef.current) {
        if (isValidUUID(data.createdBySupplierId)) {
          payload.created_by_supplier_id = data.createdBySupplierId;
        }
      }

      if (parentIdRef.current) {
        let { data: updatedRows, error } = await supabase
          .from("events")
          .update(payload)
          .eq("id", eventId)
          .select("id");

        // Anonymous fallback on parent UPDATE
        if (error && (error.code === '23503' || error.code === '42501') && payload.user_id) {
          console.warn("⚠️ FK/RLS error on parent update, retrying without user_id:", error.code);
          delete payload.user_id;
          const retry = await supabase.from("events").update(payload).eq("id", eventId).select("id");
          updatedRows = retry.data;
          error = retry.error;
        }
        if (error) throw error;

        // Proposta reivindicada por outro visitante anônimo (outro aparelho/navegador): antes de desistir, pede a
        // posse de volta. Sem isso o carrinho criava um pedido paralelo e a loja (proposta) e o carrinho (pedido
        // novo) mostravam convidados e valores diferentes. A função só transfere se o dono atual for anônimo.
        if (!updatedRows || updatedRows.length === 0) {
          const claim = await claimSharedQuote({ eventId });
          if (claim.success && (claim.claimed || claim.alreadyOwner)) {
            const retry = await supabase.from("events").update(payload).eq("id", eventId).select("id");
            if (!retry.error && retry.data && retry.data.length > 0) updatedRows = retry.data;
          }
        }

        // Nenhuma linha atualizada: o id guardado na sessão não existe mais ou não é gravável por esta sessão.
        // Só então descartamos o id e criamos um novo pedido (uma única vez, pois agora cai no INSERT).
        if (!updatedRows || updatedRows.length === 0) {
          console.warn("⚠️ Pedido da sessão não encontrado/gravável; criando um novo:", eventId);
          parentIdRef.current = null;
          sessionStorage.removeItem('ameup_parent_event_id');
          sessionStorage.removeItem('ameup_event_code');
          return await upsertParentEventInner(data, { skipReuse: true });
        }

        const childUpdate: Record<string, any> = {
          cep: data.cep,
          event_date: data.eventDate,
          guests: data.guests,
          updated_at: new Date().toISOString(),
        };
        const { error: childErr } = await supabase
          .from("events")
          .update(childUpdate)
          .eq("parent_event_id", eventId)
          .eq("is_active", true);
        if (childErr) console.warn("⚠️ Error updating children:", childErr);
      } else {
        let { error } = await supabase
          .from("events")
          .insert(payload);

        // Anonymous fallback: if FK violation on user_id (user has no profile row), retry without user_id
        if (error && (error.code === '23503' || error.code === '42501') && payload.user_id) {
          console.warn("⚠️ FK/RLS error on parent insert, retrying anonymously:", error.code);
          delete payload.user_id;
          const retry = await supabase.from("events").insert(payload);
          error = retry.error;
        }

        if (error) throw error;

        // Diagnóstico: registra por que o pedido novo foi criado (para achar a origem de pedidos paralelos).
        try {
          const search = new URLSearchParams(window.location.search);
          void recordCreationOrigin(eventId, {
            quoteMode: isQuoteMode(),
            sharedParentInSession: !!sessionStorage.getItem('ameup_shared_parent_id'),
            urlHasRef: !!(search.get('ref') || search.get('pid')),
            afterNoRows: !!opts?.skipReuse,
            firstPathSegment: window.location.pathname.split('/')[1] || '',
          });
        } catch { /* diagnóstico nunca atrapalha */ }

        // Increment referral count if this is a new event from a referral
        const refCodeId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ameup_referral_code_id') : null;
        if (refCodeId) {
          const { data: rc } = await (supabase as any).from('referral_codes').select('referral_count').eq('id', refCodeId).maybeSingle();
          if (rc) await incrementReferralCount(refCodeId, rc.referral_count || 0);
          // Clean up so we don't double-count
          sessionStorage.removeItem('ameup_referral_code_id');
        }
      }

      parentIdRef.current = eventId;
      // Publica o id já aqui (e não só nos chamadores): quem esperava o lock enxerga o pedido recém-criado.
      try {
        sessionStorage.setItem('ameup_parent_event_id', eventId);
        if (generatedCode) sessionStorage.setItem('ameup_event_code', generatedCode);
      } catch { /* sem sessionStorage */ }
      return { id: eventId, eventCode: generatedCode };
    } catch (err) {
      console.error("❌ Error upserting parent event:", err);
      return null;
    }
  }, []);

  const upsertParentEvent = useCallback(async (data: ParentEventData): Promise<{ id: string; eventCode?: string } | null> => {
    // Espera uma criação em andamento (outro componente/efeito) para reaproveitar o mesmo pedido.
    while (parentCreateLock) {
      try { await parentCreateLock; } catch { /* a falha é tratada por quem criou */ }
    }
    const needsCreate = !parentIdRef.current && !sessionStorage.getItem('ameup_parent_event_id');
    if (!needsCreate) return upsertParentEventInner(data);

    const run = upsertParentEventInner(data);
    parentCreateLock = run;
    try {
      return await run;
    } finally {
      if (parentCreateLock === run) parentCreateLock = null;
    }
  }, [upsertParentEventInner]);

  // ──────────────────────────────────────────────
  // 2a. SELECTABLE: create one child row per selected item
  //     Uses `origem` = "parentProductId:childItemId" for lookups
  // ──────────────────────────────────────────────
  const upsertSelectableChildren = useCallback(async (
    cartItem: CartItem,
    parentEventId: string,
    userId?: string,
    parentStatus?: string | null
  ): Promise<string | null> => {
    try {
      const selectedMenuItems = cartItem.selectedMenuItems || {};
      const childItemIds = Object.keys(selectedMenuItems);

      if (childItemIds.length === 0) return null;

      const ownerId = await resolveOwnerUserId(userId);

      // Fetch child item details from supplier_menu
      const { data: childMenuData } = await (supabase as any)
        .from("supplier_menu")
        .select("id, name, fixed_price, event_hour_time, category")
        .in("id", childItemIds);

      const childMenuMap = new Map<string, any>();
      (childMenuData || []).forEach((item: any) => childMenuMap.set(item.id, item));

      const parentProductId = cartItem.productId;

      // Deactivate existing children for this selectable parent that are no longer selected
      const { data: existingChildren } = await supabase
        .from("events")
        .select("id, origem")
        .eq("parent_event_id", parentEventId)
        .like("origem", `${parentProductId}:%`)
        .eq("is_active", true);

      const activeOrigens = new Set(
        childItemIds.map(childId => `${parentProductId}:${childId}`)
      );

      const toDeactivate = (existingChildren || []).filter(
        (row: any) => !activeOrigens.has(row.origem)
      );
      if (toDeactivate.length > 0) {
        await supabase
          .from("events")
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .in("id", toDeactivate.map((r: any) => r.id));

      }

      // Upsert each selected item as a separate child row
      let firstChildId: string | null = null;
      for (const childItemId of childItemIds) {
        const quantity = selectedMenuItems[childItemId] || 1;
        const menuInfo = childMenuMap.get(childItemId);
        const itemName = menuInfo?.name || 'Item selecionado';
        const itemPrice = (menuInfo?.fixed_price || 0) * quantity;
        const itemEventHourTime = menuInfo?.event_hour_time || cartItem.eventHourTime || null;
        const origem = `${parentProductId}:${childItemId}`;

        let existing: { id: string; status?: string | null } | null = null;
        const { data: existingData } = await supabase
          .from("events")
          .select("id, status")
          .eq("parent_event_id", parentEventId)
          .eq("origem", origem)
          .eq("is_active", true)
          .limit(1);
        existing = existingData && existingData.length > 0 ? existingData[0] : null;

        const childId = existing?.id || crypto.randomUUID();
        if (!firstChildId) firstChildId = childId;

        const childPayload: Record<string, any> = {
          id: childId,
          parent_event_id: parentEventId,
          event_code: null,          // Only parent has event_code
          origem: origem,            // Product reference for lookups
          supplier_id: isValidUUID(cartItem.supplierId) ? cartItem.supplierId : null,
          user_id: isValidUUID(ownerId) ? ownerId : (isValidUUID(userId) ? userId : null),
          menu: itemName,
          party_time: itemEventHourTime,
          selected_categories: [cartItem.category],
          calculated_category: itemPrice.toString(),
          calculated_guest: (menuInfo?.fixed_price || 0).toString(),
          calculated_total: itemPrice || 0,
          total_value: itemPrice || 0,
          item_quantity: quantity.toString(),
          guests: cartItem.guests,
          cep: cartItem.cep,
          event_date: cartItem.date ? cartItem.date.split("T")[0] : null,
          ...(existing ? {} : { 
            status: isQuoteMode() ? "Proposta Fornecedor" : "Escolheu itens",
            geral_status: isQuoteMode() ? "Proposta Fornecedor" : "Escolheu itens"
          }),
          updated_at: new Date().toISOString(),
        };

        if (existing) {
          let { error } = await supabase
            .from("events")
            .update(childPayload)
            .eq("id", childId);
          if (error && (error.code === '23503' || error.code === '42501') && childPayload.user_id) {
            console.warn("⚠️ FK/RLS error on selectable child update, retrying without user_id");
            childPayload.user_id = null;
            const retry = await supabase.from("events").update(childPayload).eq("id", childId);
            error = retry.error;
          }
          if (error) throw error;
        } else {
          childPayload.created_at = new Date().toISOString();
          let { error } = await supabase
            .from("events")
            .insert(childPayload);

          // Anonymous fallback for child items
          if (error && (error.code === '23503' || error.code === '42501') && childPayload.user_id) {
            console.warn("⚠️ FK/RLS error on selectable child insert, retrying anonymously");
            childPayload.user_id = null;
            const retry = await supabase.from("events").insert(childPayload);
            error = retry.error;
          }

          if (error) throw error;
        }

      }

      return firstChildId;
    } catch (err) {
      console.error("❌ Error upserting selectable children:", err);
      toast({
        title: "Erro ao salvar itens selecionados",
        description: "Não foi possível salvar os itens do cardápio selecionável. Tente novamente.",
        variant: "destructive",
      });
      return null;
    }
  }, []);

  // ──────────────────────────────────────────────
  // 2b. CHILD: create/update item row linked to parent
  //     Uses `origem` = productId for lookups (no event_code on children)
  // ──────────────────────────────────────────────
  const upsertChildItem = useCallback(async (
    cartItem: CartItem,
    parentEventId: string,
    userId?: string,
    parentStatus?: string | null
  ): Promise<string | null> => {
    // Serialize concurrent calls for the same (parent, origem) to prevent duplicate inserts
    const lockKey = `${parentEventId}::${cartItem.productId || 'unknown'}`;
    const existingLock = childUpsertLocks.get(lockKey);
    if (existingLock) {
      try { await existingLock; } catch {}
    }

    const run = async (): Promise<string | null> => {
    try {
      // For selectable menus, delegate to upsertSelectableChildren
      if (cartItem.isSelectableMenu && cartItem.selectedMenuItems && Object.keys(cartItem.selectedMenuItems).length > 0) {
        return await upsertSelectableChildren(cartItem, parentEventId, userId, parentStatus);
      }

      const productId = cartItem.productId;
      const menuName = cartItem.itemName || cartItem.productName || null;
      const ownerId = await resolveOwnerUserId(userId);

      let existing: { id: string; status?: string | null } | null = null;
      if (productId) {
        const { data } = await supabase
          .from("events")
          .select("id, status")
          .eq("parent_event_id", parentEventId)
          .eq("origem", productId)
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1);
        existing = data && data.length > 0 ? data[0] : null;
      }

      const childId = existing?.id || crypto.randomUUID();
      const pricePerGuest = cartItem.pricePerPerson || 0;

      const childPayload: Record<string, any> = {
        id: childId,
        parent_event_id: parentEventId,
        event_code: null,            // Only parent has event_code
        origem: productId,           // Product reference for lookups
        supplier_id: isValidUUID(cartItem.supplierId) ? cartItem.supplierId : null,
        user_id: isValidUUID(ownerId) ? ownerId : (isValidUUID(userId) ? userId : null),
        menu: menuName,
        party_time: cartItem.eventHourTime || null,
        selected_categories: [cartItem.category],
        calculated_category: cartItem.totalPrice.toString(),
        calculated_guest: pricePerGuest.toString(),
        calculated_total: cartItem.totalPrice || 0,
        total_value: cartItem.totalPrice || 0,
        item_quantity: (cartItem.selectedItemsCount || 1).toString(),
        guests: cartItem.guests,
        cep: cartItem.cep,
        event_date: cartItem.date ? cartItem.date.split("T")[0] : null,
        substitutions: cartItem.substitutions ? cartItem.substitutions : (cartItem.categorySelections ? cartItem.categorySelections : null),
        // NOTE: optional items are persisted in the parent's menu summary (selectedOptionalItems)
        ...buildEventHourFields(cartItem),
        ...(existing ? {} : { 
          status: isQuoteMode() ? "Proposta Fornecedor" : "Escolheu itens",
          geral_status: isQuoteMode() ? "Proposta Fornecedor" : "Escolheu itens"
        }),
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        let { error } = await supabase
          .from("events")
          .update(childPayload)
          .eq("id", childId);
        if (error && (error.code === '23503' || error.code === '42501') && childPayload.user_id) {
          console.warn("⚠️ FK/RLS error on child update, retrying without user_id");
          childPayload.user_id = null;
          const retry = await supabase.from("events").update(childPayload).eq("id", childId);
          error = retry.error;
        }
        if (error) throw error;
      } else {
        childPayload.created_at = new Date().toISOString();
        let { error } = await supabase
          .from("events")
          .insert(childPayload);

        // Anonymous fallback for regular child items
        if (error && (error.code === '23503' || error.code === '42501') && childPayload.user_id) {
          console.warn("⚠️ FK/RLS error on child insert, retrying anonymously");
          childPayload.user_id = null;
          const retry = await supabase.from("events").insert(childPayload);
          error = retry.error;
        }

        if (error) throw error;
      }

      return childId;
    } catch (err) {
      console.error("❌ Error upserting child item:", err);
      return null;
    }
    };

    const promise = run();
    childUpsertLocks.set(lockKey, promise);
    try {
      return await promise;
    } finally {
      // Clear lock only if it's still ours
      if (childUpsertLocks.get(lockKey) === promise) {
        childUpsertLocks.delete(lockKey);
      }
    }
  }, [upsertSelectableChildren]);

  // ──────────────────────────────────────────────
  // 3. Sync all cart items as children + update parent totals
  // ──────────────────────────────────────────────
  const syncCartToSupabase = useCallback(async (
    cartItems: CartItem[],
    parentEventId: string,
    financialSummary?: { subtotal: number; freight?: number; total?: number; discount_code?: string; discount_value?: number },
    userId?: string,
    options?: { allowEmpty?: boolean }
  ) => {
    try {
      // Carrinho vazio NUNCA apaga o pedido salvo: o cliente que volta pelo link da proposta em outra sessao (carrinho local ainda
      // vazio) zerava cardapio e valor do pai (MAR-A2E0: menu [] e R$ 0 com os itens ainda nos filhos). So esvazia quando o
      // cliente remove o ultimo item de proposito (`allowEmpty`).
      if (cartItems.length === 0 && !options?.allowEmpty) return;
      if (await isOrderSold(parentEventId)) return;

      // Rede de segurança: se o pai nasceu sem fornecedor (contexto ainda não
      // carregado no simulador), corrige antes que o cliente chegue ao checkout.
      const cartSupplierId = cartItems.find(i => isValidUUID(i.supplierId))?.supplierId;
      await backfillParentSupplier(parentEventId, cartSupplierId);

      const childPromises = cartItems.map(item =>
        upsertChildItem(item, parentEventId, userId)
      );
      await Promise.all(childPromises);

      const subtotal = financialSummary?.subtotal ?? cartItems.reduce((sum, i) => sum + i.totalPrice, 0);
      
      // Attempt to recover existing freight/discount if not provided in financialSummary
      let freight = financialSummary?.freight;
      let discountValue = financialSummary?.discount_value;
      let discountCode = financialSummary?.discount_code;

      if (freight === undefined || discountValue === undefined) {
        const { data: currentEvent } = await supabase
          .from("events")
          .select("delivery_fee, discount_value, discount_code")
          .eq("id", parentEventId)
          .maybeSingle();
        
        if (currentEvent) {
          if (freight === undefined) freight = Number(currentEvent.delivery_fee) || 0;
          if (discountValue === undefined) discountValue = Number(currentEvent.discount_value) || 0;
          if (discountCode === undefined) discountCode = currentEvent.discount_code || undefined;
        } else {
          if (freight === undefined) freight = 0;
          if (discountValue === undefined) discountValue = 0;
        }
      }

      // Sem cupom informado nesta gravação o banco mantém o que já estava (abaixo), então o total tem de usar esse mesmo desconto:
      // antes o total vinha do carrinho sem cupom (R$ 3.430 -> R$ 4.029,80) e o cupom de R$ 600 ficava gravado sem abater nada.
      const couponTouched = (financialSummary?.discount_code !== undefined && financialSummary?.discount_code !== null)
        || (financialSummary?.discount_value !== undefined && financialSummary?.discount_value !== null);
      const total = couponTouched && financialSummary?.total !== undefined
        ? financialSummary.total
        : Math.max(0, subtotal + (freight || 0) - (discountValue || 0));

      const latestItem = cartItems[0];
      const allCategories = [...new Set(cartItems.map(i => i.category).filter(Boolean))];
      const totalGuests = parseInt(latestItem?.guests || '0', 10);
      const pricePerGuest = totalGuests > 0 ? (subtotal / totalGuests) : 0;
      // For selectable menus, extract selected item names from the summary string
      const menuSummaryData = cartItems.map(i => {
        let resolvedSelectedNames: Record<string, string> | undefined;
        
        if (i.isSelectableMenu && i.selectedMenuItems && Object.keys(i.selectedMenuItems).length > 0) {
          // Parse the itemName summary (e.g. "2x Dadinho de Tapioca • 1x Caldinho de Feijão")
          const summary = i.itemName || '';
          const parts = summary.split('•').map(p => p.trim()).filter(Boolean);
          resolvedSelectedNames = {};
          const itemIds = Object.keys(i.selectedMenuItems);
          
          parts.forEach((part, idx) => {
            // Remove quantity prefix like "2x "
            const nameMatch = part.replace(/^\d+x\s*/, '').trim();
            if (nameMatch && idx < itemIds.length) {
              resolvedSelectedNames![itemIds[idx]] = nameMatch;
            }
          });
          
          // Fallback: if parsing didn't work well, use the full summary for each ID
          if (Object.keys(resolvedSelectedNames).length === 0 && summary) {
            itemIds.forEach(id => {
              resolvedSelectedNames![id] = summary;
            });
          }
        }
        
        return { item: i, resolvedSelectedNames };
      });

      const menuSummary = menuSummaryData.map(({ item: i, resolvedSelectedNames }) => {
        const base: Record<string, any> = {
          productId: i.productId,
          name: i.itemName || i.productName,
          category: i.category,
          totalPrice: i.totalPrice,
          quantity: i.selectedItemsCount || 1,
        };
        if (i.substitutions && Object.keys(i.substitutions).length > 0) {
          base.substitutions = i.substitutions;
        }
        // Itens incluídos pelo fornecedor na proposta (mode=orcamento)
        if (i.additions && Object.keys(i.additions).length > 0) {
          base.additions = i.additions;
        }
        if (i.isSelectableMenu && i.selectedMenuItems && Object.keys(i.selectedMenuItems).length > 0) {
          base.isSelectableMenu = true;
          base.selectedMenuItems = i.selectedMenuItems;
          base.selectedItemNames = resolvedSelectedNames || {};
        }
        // Horas adicionais escolhidas na proposta: sem isto o link, o checkout e a página da proposta perdem as horas e o valor delas.
        if ((Number(i.extraHours) || 0) > 0) {
          base.extraHours = Number(i.extraHours);
          base.hourExtensionAmount = Number(i.hourExtensionAmount) || 0;
        }
        if (i.selectedOptionalItems && i.selectedOptionalItems.length > 0) {
          base.selectedOptionalItems = i.selectedOptionalItems;
          // Opções por quantidade (ex.: Pizza sem Glúten, 4 pessoas): o cliente e o fornecedor veem as mesmas unidades.
          const quantities = normalizeOptionalQuantities(i.optionalQuantities);
          const chosen = Object.fromEntries(i.selectedOptionalItems.filter((id: string) => quantities[id]).map((id: string) => [id, quantities[id]]));
          if (Object.keys(chosen).length > 0) base.optionalQuantities = chosen;
        }
        // Save category selections (maxChoices and substitutions)
        if (
          (i.categorySelections && Object.values(i.categorySelections).some(arr => arr.length > 0)) ||
          (i.substitutions && Object.keys(i.substitutions).length > 0)
        ) {

          base.categorySelections = i.categorySelections;
          
          // Build selectedItemNames from categorySelections and substitutions so contract can find them
          if (!base.selectedItemNames) {
            const allNames: Record<string, string> = {};
            
            // From category selections
            if (i.categorySelections) {
              Object.values(i.categorySelections).forEach((items, catIdx) => {
                items.forEach((name, idx) => {
                  allNames[`cat${catIdx}_${idx}`] = name;
                });
              });
            }
            
            // From substitutions
            if (i.substitutions) {
              Object.entries(i.substitutions).forEach(([original, subData]: [string, any], idx) => {
                allNames[`sub_${idx}`] = subData.substituteItem;
              });
            }
            
            if (Object.keys(allNames).length > 0) {
              base.selectedItemNames = allNames;
            }
          }
        } else {

        }
        return base;
      });

      // Build substitutions summary for the dedicated column
      const allSubstitutions: Record<string, any> = {};
      for (const item of cartItems) {
        if (item.substitutions && Object.keys(item.substitutions).length > 0) {
          allSubstitutions[item.productId] = {
            productName: item.itemName || item.productName,
            substitutions: item.substitutions,
          };
        }
        if (item.additions && Object.keys(item.additions).length > 0) {
          allSubstitutions[item.productId] = {
            ...(allSubstitutions[item.productId] || { productName: item.itemName || item.productName }),
            additions: item.additions,
          };
        }
      }


      
      const parentUpdate: Record<string, any> = {
        calculated_total: subtotal,
        total_value: total,
        event_value_total: String(total),
        delivery_fee: freight,
        // Adicional do fornecedor (já somado em delivery_fee): guardado à parte para o carrinho do cliente não perdê-lo.
        ...freightExtraColumns(cartItems),
        menu: menuSummary,
        selected_categories: allCategories,
        calculated_guest: pricePerGuest.toFixed(2),
        calculated_category: subtotal.toString(),
        item_quantity: cartItems.length.toString(),
        updated_at: new Date().toISOString(),
        substitutions: Object.keys(allSubstitutions).length > 0 ? allSubstitutions : null,
        is_pickup: cartItems.some(i => i.isPickup),
        // Categorias com preço especial (crianças, staff...) congeladas na venda.
        special_guests: buildSpecialGuestsSnapshot(cartItems, (i) => getCartItemBasePricePerGuest(i, readSessionCheckoutTax())),
      };
      const selectedEventType = cartItems.find(item => item.eventType?.trim())?.eventType?.trim()
        || sessionStorage.getItem('ameup_event_type')?.trim();
      if (selectedEventType) parentUpdate.event_type = selectedEventType;
      if (latestItem?.cep) parentUpdate.cep = latestItem.cep;
      if (latestItem?.date) parentUpdate.event_date = latestItem.date.split("T")[0];
      if (latestItem?.guests) parentUpdate.guests = latestItem.guests;
      // Persist discount fields using the resolved values (which already
      // recover from DB when the caller does not provide them explicitly).
      // This prevents accidental wipe of an active coupon when later cart
      // operations sync without re-passing the discount summary.
      if (couponTouched) {
        // Caller explicitly set (or cleared) the coupon — honor that intent.
        parentUpdate.discount_code = financialSummary.discount_code ?? null;
        parentUpdate.discount_value = Number(financialSummary.discount_value) || 0;
      } else {
        // Caller did not touch the coupon — keep whatever is currently in DB.
        parentUpdate.discount_code = discountCode ?? null;
        parentUpdate.discount_value = Number(discountValue) || 0;
      }

      // Replicate party_time from first cart item to parent event
      const firstPartyTime = cartItems.find(i => i.eventHourTime)?.eventHourTime;
      if (firstPartyTime) parentUpdate.party_time = firstPartyTime;

      // Congela no evento se a duração é um teto ("até X horas") e a antecedência
      // de chegada da equipe — usados na cláusula 2 do contrato.
      const itemWithHourFlag = cartItems.find(i => i.eventHourTime);
      if (itemWithHourFlag) parentUpdate.event_hour_is_max = !!itemWithHourFlag.eventHourIsMax;
      const itemWithArrival = cartItems.find(i => i.hourArrived);
      if (itemWithArrival) parentUpdate.supplier_arrival_time = itemWithArrival.hourArrived;

      // Horas adicionais contratadas: replicadas do item que as possui
      // Sem nenhuma hora extra no carrinho (o fornecedor tirou as horas), limpa as colunas: o total já voltou ao valor sem horas.
      const itemWithExtraHours = cartItems.find(i => (Number(i.extraHours) || 0) > 0);
      Object.assign(parentUpdate, buildEventHourFields(itemWithExtraHours));

      let { error } = await supabase
        .from("events")
        .update(parentUpdate)
        .eq("id", parentEventId);

      // Coluna do adicional de frete ainda não criada no banco: grava o resto (inclusive o frete total) sem ela.
      if (error && 'freight_extra' in parentUpdate && /freight_extra/i.test(`${error.message} ${(error as any).details ?? ''}`)) {
        console.warn('freight_extra indisponível no banco; gravando o pedido sem o adicional separado.');
        const { freight_extra: _ignored, ...withoutExtra } = parentUpdate;
        ({ error } = await supabase.from("events").update(withoutExtra).eq("id", parentEventId));
      }

      if (error) throw error;

      // Update parent status only if it's still in an early funnel stage
      // (avoid overwriting "Login" or later statuses set by checkout flow)
      const { data: currentEvent } = await supabase
        .from("events")
        .select("status")
        .eq("id", parentEventId)
        .maybeSingle();
      const earlyStatuses = ['Acessou Página', 'Preencheu o simulador', 'Entrou no Cardápio', 'Proposta Fornecedor'];
      if (currentEvent && earlyStatuses.includes(currentEvent.status || '') && !isQuoteMode()) {
        await supabase
          .from("events")
          .update({ 
            status: "Escolheu itens", 
            geral_status: "Escolheu itens",
            updated_at: new Date().toISOString() 
          })
          .eq("id", parentEventId);
      } else if (currentEvent && isQuoteMode()) {
        // Ensure status stays as "Proposta Fornecedor" during supplier editing
        await supabase
          .from("events")
          .update({ 
            status: "Proposta Fornecedor", 
            geral_status: "Proposta Fornecedor",
            updated_at: new Date().toISOString() 
          })
          .eq("id", parentEventId);
      }

      // Persist substitutions to supplier_menu_substitutions with event_code
      const subsItems = cartItems.filter(i => i.substitutions && Object.keys(i.substitutions).length > 0);
      if (subsItems.length > 0) {
        try {
          // Get event_code from parent event
          let eventCode: string | null = null;
          try {
            const { data: parentEvt } = await supabase
              .from("events")
              .select("event_code")
              .eq("id", parentEventId)
              .maybeSingle();
            eventCode = parentEvt?.event_code || null;
          } catch {
            // Fallback: try sessionStorage
            eventCode = sessionStorage.getItem('ameup_event_code') || null;
          }
          if (!eventCode) {
            eventCode = sessionStorage.getItem('ameup_event_code') || null;
          }

          for (const item of subsItems) {
            if (!item.substitutions) continue;

            // Delete existing substitutions for this menu+event_code to avoid duplicates
            if (eventCode) {
              await (supabase as any)
                .from("supplier_menu_substitutions")
                .delete()
                .eq("menu_id", item.productId)
                .eq("event_code", eventCode);
            }

            for (const [originalItem, sub] of Object.entries(item.substitutions)) {
              await (supabase as any)
                .from("supplier_menu_substitutions")
                .insert({
                  menu_id: item.productId,
                  original_item_name: originalItem,
                  substitute_item_name: sub.substituteItem,
                  additional_cost: sub.additionalCost || 0,
                  is_active: true,
                  event_code: eventCode,
                });
            }
          }
        } catch (subErr) {
          console.warn("⚠️ Failed to persist substitutions:", subErr);
        }
      }

      return { subtotal, freight, total };
    } catch (err) {
      console.error("❌ Error syncing cart:", err);
      return null;
    }
  }, [upsertChildItem]);

  // ──────────────────────────────────────────────
  // 4. Remove a child item (handles both regular and selectable)
  //    Uses `origem` instead of `event_code` for lookups
  // ──────────────────────────────────────────────
  const removeChildItem = useCallback(async (
    productId: string,
    parentEventId: string
  ) => {
    try {
      // Regular items: origem = productId
      const { data: exactChildren } = await supabase
        .from("events")
        .select("id")
        .eq("parent_event_id", parentEventId)
        .eq("origem", productId)
        .eq("is_active", true);

      // Selectable items: origem = "productId:childItemId"
      const { data: selectableChildren } = await supabase
        .from("events")
        .select("id")
        .eq("parent_event_id", parentEventId)
        .like("origem", `${productId}:%`)
        .eq("is_active", true);

      const allChildren = [...(exactChildren || []), ...(selectableChildren || [])];

      if (allChildren.length > 0) {
        await supabase
          .from("events")
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .in("id", allChildren.map(c => c.id));
      } else {
        // Item da proposta do fornecedor: o cliente do link não consegue ler nem alterar esses itens (RLS).
        // A função do banco confere se é proposta compartilhada ainda não paga e desativa (sem apagar).
        const { error: rpcError } = await (supabase as any).rpc("remove_proposal_child", {
          p_parent_event_id: parentEventId,
          p_product_id: productId,
        });
        if (rpcError) console.warn("⚠️ Could not remove proposal item:", rpcError.message);
      }
    } catch (err) {
      console.error("❌ Error removing child item:", err);
    }
  }, []);

  // ──────────────────────────────────────────────
  // 5. Mark share = true
  // ──────────────────────────────────────────────
  const markAsShared = useCallback(async (eventId: string, supplierId?: string) => {
    try {
      const updatePayload: Record<string, any> = {
        share: true,
        updated_at: new Date().toISOString(),
      };
      // Ensure supplier traceability: if supplier_id is provided and not yet set, fill it
      if (isValidUUID(supplierId)) {
        updatePayload.created_by_supplier_id = supplierId;
        updatePayload.supplier_id = supplierId;
      }
      await supabase
        .from("events")
        .update({
          ...updatePayload,
          status: 'Proposta Fornecedor',
          geral_status: 'Proposta Fornecedor'
        })
        .eq("id", eventId);
    } catch (err) {
      console.error("❌ Error marking as shared:", err);
    }
  }, []);

  // ──────────────────────────────────────────────
  // 6. Load existing children for a parent
  // ──────────────────────────────────────────────
  const loadChildItems = useCallback(async (parentEventId: string) => {
    try {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("parent_event_id", parentEventId)
        .eq("is_active", true);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("❌ Error loading child items:", err);
      return [];
    }
  }, []);

  const setParentEventId = useCallback((id: string | null) => {
    parentIdRef.current = id;
  }, []);

  const getParentEventId = useCallback(() => parentIdRef.current, []);

  return {
    upsertParentEvent,
    upsertChildItem,
    syncCartToSupabase,
    removeChildItem,
    markAsShared,
    loadChildItems,
    setParentEventId,
    getParentEventId,
  };
};
